import { zodResolver } from "@hookform/resolvers/zod";
import { resetPasswordConfirm } from "@stryker/core/api/accounts";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { z } from "zod";
import ArrowIcon from "@/assets/icons/arrow.svg?react";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import EyeIcon from "@/assets/icons/eye-open.svg?react";
import LockIcon from "@/assets/icons/lock.svg?react";
import TickIcon from "@/assets/icons/tick.svg?react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { hasSpecialSymbolRegex } from "@/constants/regex";
import useLocationQuery from "@/hooks/useLocationQuery";
import useAuthStore from "@/store/useAuthStore";
import { setAccessToken, setRefreshToken } from "@/utils/storageService";

const setNewPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, { message: "Minimal password length is 8." })
      .regex(hasSpecialSymbolRegex, "Must contain one special character")
      .nonempty("Password is required"),
    confirmNewPassword: z.string().nonempty("Password is required"),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Your passwords don’t match. Please, check the data and try again",
    path: ["confirmNewPassword"],
  });

type SetNewPasswordSchema = z.infer<typeof setNewPasswordSchema>;
export default function SetNewPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const query = useLocationQuery();
  const isSuccess = query.get("success");
  const token = query.get("token");
  const email = query.get("email");

  const {
    mutate,
    isPending,
    error: apiError,
  } = useMutation({
    mutationFn: resetPasswordConfirm,
    onSuccess: (response) => {
      navigate(`/set-new-password?success=true&access=${response.access}&refresh=${response.refresh}`, {
        replace: true,
      });
    },
    onError: (error: string) => {
      return error;
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetNewPasswordSchema>({
    resolver: zodResolver(setNewPasswordSchema),
  });

  const onReset = (model: { newPassword: string; confirmNewPassword: string }) => {
    if (!email || !token) return;
    mutate({ email, token, password: model.newPassword, password2: model.confirmNewPassword });
    navigate(`/set-new-password?success=true`, { replace: true });
  };

  if ((!email || !token) && !isSuccess) {
    return <Navigate to="/login" />;
  }

  if (isSuccess) {
    return <PasswordResetSuccess />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="flex w-[360px] flex-col">
        <div className="mb-5 self-center rounded-lg border border-solid border-gray-200 p-3">
          <LockIcon />
        </div>
        <Typography variant="display-sm" weight="semibold" className="text-center">
          Set new password
        </Typography>
        <Typography variant="text-md" weight="regular" className="mb-5 text-center">
          Your new password must be different to previously used passwords.
        </Typography>
        <form onSubmit={handleSubmit(onReset)}>
          <TextInput
            className="mb-5"
            label="New Password"
            placeholder="Enter your new password"
            type={showPassword ? "text" : "password"}
            error={errors.newPassword?.message}
            {...register("newPassword")}
            endAdornment={
              showPassword ? (
                <EyeOffIcon onClick={() => setShowPassword(false)} />
              ) : (
                <EyeIcon onClick={() => setShowPassword(true)} />
              )
            }
          />
          <TextInput
            label="Confirm password"
            placeholder="Confirm new password"
            type={showConfirmPassword ? "text" : "password"}
            error={errors.confirmNewPassword?.message}
            {...register("confirmNewPassword")}
            endAdornment={
              showConfirmPassword ? (
                <EyeOffIcon onClick={() => setShowConfirmPassword(false)} />
              ) : (
                <EyeIcon onClick={() => setShowConfirmPassword(true)} />
              )
            }
          />
          <div className="mt-5">
            <div className="mb-4 flex items-center gap-2">
              <TickIcon />
              <Typography variant="text-small" weight="medium">
                Must be at least 8 characters
              </Typography>
            </div>
            <div className="mb-4 flex items-center gap-2">
              <TickIcon />
              <Typography variant="text-small" weight="medium">
                Must contain one special character
              </Typography>
            </div>
          </div>
          {apiError && (
            <Typography variant="text-small" weight="bold" className="text-error-500">
              {apiError}
            </Typography>
          )}
          <div className="mb-8 mt-6 flex flex-col gap-2">
            <Button type="submit" disabled={isPending}>
              Reset password
            </Button>
            <Button
              onClick={() => {
                navigate("/login");
              }}
              type="button"
              variant="ghost"
              className="flex items-center justify-center gap-2"
            >
              <ArrowIcon />
              Back to Login
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordResetSuccess() {
  const navigate = useNavigate();
  const query = useLocationQuery();
  const access = query.get("access");
  const refresh = query.get("refresh");
  const { setIsAuthorized } = useAuthStore();
  const loginMagically = () => {
    if (access && refresh) {
      setAccessToken(access);
      setRefreshToken(refresh);
      setIsAuthorized(true);
      navigate("/");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="flex w-[360px] flex-col">
        <div className="mb-5 self-center rounded-lg border border-solid border-gray-200 p-3">
          <TickIcon />
        </div>
        <Typography variant="display-sm" weight="semibold" className="text-center">
          Password reset
        </Typography>
        <Typography variant="text-md" weight="regular" className="text-center">
          Your password has been successfully reset. Click below to log in magically.
        </Typography>
        <div className="mb-8 mt-5 flex flex-col items-center gap-2">
          <Button className="w-full" onClick={loginMagically}>
            Continue
          </Button>
          <Button
            onClick={() => {
              navigate("/login");
            }}
            type="button"
            variant="ghost"
            className="flex items-center justify-center gap-2"
          >
            <ArrowIcon />
            Back to Log in
          </Button>
        </div>
      </div>
    </div>
  );
}
